import React, { useState, useEffect, useCallback } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Package, AlertTriangle, Truck, DollarSign } from "lucide-react";
import { COLORS, apiFetch, cardStyle, inputStyle, PageHeader, LoadingSpinner, EmptyState, useMoneda } from "../shared/ui";
import { fechaLocalISO, haceDiasISO } from "../shared/fechas";
import { fmtMoneda } from "../shared/moneda";

const fmtNum = (v, dec = 0) =>
  v == null ? "—" : Number(v).toLocaleString("es", { maximumFractionDigits: dec });

function KpiCard({ label, kpi, formato = fmtNum, sufijo = "", invertir = false }) {
  const { actual, anterior } = kpi || {};
  let delta = null;
  if (actual != null && anterior != null && anterior !== 0) {
    delta = ((actual - anterior) / Math.abs(anterior)) * 100;
  }
  const positivo = delta != null && (invertir ? delta < 0 : delta > 0);
  return (
    <div style={{ ...cardStyle, padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
        {formato(actual)}{actual != null && sufijo}
      </div>
      {delta != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 12, fontWeight: 600, color: positivo ? COLORS.success : COLORS.danger }}>
          {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          {Math.abs(delta).toFixed(1)}% vs período anterior
        </div>
      )}
    </div>
  );
}

function Grupo({ icon: Icon, titulo, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: 8, color: COLORS.text, fontSize: 15, fontWeight: 700, margin: "0 0 12px" }}>
        <Icon size={16} color={COLORS.primary} /> {titulo}
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

const PRESETS = [{ d: 30, l: "30 días" }, { d: 60, l: "60 días" }, { d: 90, l: "90 días" }];

export default function KpisPage({ token }) {
  const { simbolo } = useMoneda();
  const fmtMoney = useCallback((v) => fmtMoneda(v, simbolo), [simbolo]);
  const [dias, setDias] = useState(30);
  const [bodega, setBodega] = useState("");
  const [bodegas, setBodegas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/bodegas", {}, token).then(r => setBodegas(r.data || r || [])).catch(() => {});
  }, [token]);

  const cargar = useCallback(() => {
    setLoading(true);
    const hasta = fechaLocalISO();
    const desde = haceDiasISO(dias - 1);
    const q = `?fecha_desde=${desde}&fecha_hasta=${hasta}` + (bodega ? `&id_bodega=${encodeURIComponent(bodega)}` : "");
    apiFetch(`/bi/kpis${q}`, {}, token)
      .catch(() => null)
      .then(d => { setData(d); setLoading(false); });
  }, [token, dias, bodega]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <EmptyState message="No hay datos de KPIs disponibles" />;
  const { ventas, inventario, disponibilidad, cadena_suministro } = data;

  return (
    <div>
      <PageHeader title="KPIs" subtitle={`${data.periodo.desde} → ${data.periodo.hasta}`} action={
        <div style={{ display: "flex", gap: 8 }}>
          {PRESETS.map(p => (
            <button key={p.d} onClick={() => setDias(p.d)}
              style={{ padding: "7px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${dias === p.d ? COLORS.primary : COLORS.border}`,
                background: dias === p.d ? COLORS.primarySoft : COLORS.surface,
                color: dias === p.d ? COLORS.primary : COLORS.textMuted }}>
              {p.l}
            </button>
          ))}
          <select value={bodega} onChange={e => setBodega(e.target.value)} style={{ ...inputStyle, width: 180, padding: "7px 10px" }}>
            <option value="">Todas las bodegas</option>
            {bodegas.map(b => <option key={b.id_bodega} value={b.id_bodega}>{b.descripcion || b.id_bodega}</option>)}
          </select>
        </div>
      } />

      <Grupo icon={DollarSign} titulo="Ventas y margen">
        <KpiCard label="Ingresos" kpi={ventas.ingresos} formato={fmtMoney} />
        <KpiCard label="Unidades vendidas" kpi={ventas.unidades} />
        <KpiCard label="Margen bruto" kpi={ventas.margen_bruto} formato={fmtMoney} />
        <KpiCard label="Margen %" kpi={ventas.margen_pct} formato={v => fmtNum(v, 1)} sufijo="%" />
        <KpiCard label="Venta diaria prom." kpi={ventas.venta_diaria_promedio} formato={fmtMoney} />
        <KpiCard label="GMROI" kpi={ventas.gmroi} formato={v => fmtNum(v, 2)} />
        <KpiCard label="Sell-through" kpi={ventas.sell_through_pct} formato={v => fmtNum(v, 1)} sufijo="%" />
      </Grupo>

      <Grupo icon={Package} titulo="Inventario">
        <KpiCard label="Valor a costo" kpi={inventario.valor_costo} formato={fmtMoney} />
        <KpiCard label="Rotación anualizada" kpi={inventario.rotacion_anualizada} formato={v => fmtNum(v, 2)} />
        <KpiCard label="Días de inventario" kpi={inventario.dias_inventario} formato={v => fmtNum(v, 1)} invertir />
        <KpiCard label="SKUs sin mov. +90d" kpi={inventario.sin_movimiento_90} invertir />
        <KpiCard label="Valor sin mov. +90d" kpi={inventario.valor_sin_movimiento_90} formato={fmtMoney} invertir />
        <KpiCard label="Exceso (cobertura >90d)" kpi={inventario.exceso_valor_costo} formato={fmtMoney} invertir />
      </Grupo>

      <Grupo icon={AlertTriangle} titulo="Disponibilidad">
        <KpiCard label="SKUs en quiebre" kpi={disponibilidad.skus_en_quiebre} invertir />
        <KpiCard label="Tasa de quiebre" kpi={disponibilidad.tasa_quiebre_pct} formato={v => fmtNum(v, 1)} sufijo="%" invertir />
        <KpiCard label="Venta perdida est." kpi={disponibilidad.venta_perdida_estimada} formato={fmtMoney} invertir />
        <KpiCard label="Fill rate traslados" kpi={disponibilidad.fill_rate_traslados_pct} formato={v => fmtNum(v, 1)} sufijo="%" />
      </Grupo>

      <Grupo icon={Truck} titulo="Cadena de suministro">
        <KpiCard label="Traslados sugeridos" kpi={cadena_suministro.traslados_sugeridos} />
        <KpiCard label="Tasa aprobación" kpi={cadena_suministro.tasa_aprobacion_pct} formato={v => fmtNum(v, 1)} sufijo="%" />
        <KpiCard label="Tasa cancelación" kpi={cadena_suministro.tasa_cancelacion_pct} formato={v => fmtNum(v, 1)} sufijo="%" invertir />
        <KpiCard label="Traslados vencidos" kpi={cadena_suministro.traslados_vencidos} invertir />
        <KpiCard label="Ciclo promedio (días)" kpi={cadena_suministro.ciclo_promedio_dias} formato={v => fmtNum(v, 1)} invertir />
        <KpiCard label="Edad sugerencias (días)" kpi={cadena_suministro.edad_sugerencias_dias} formato={v => fmtNum(v, 1)} invertir />
      </Grupo>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 14, fontWeight: 700, margin: "0 0 14px" }}>Ingresos y margen diarios</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.tendencia_diaria}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="fecha" tick={{ fontSize: 10 }} tickFormatter={f => f?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="ingresos" stroke={COLORS.primary} dot={false} name="Ingresos" />
              <Line type="monotone" dataKey="margen" stroke={COLORS.success} dot={false} name="Margen" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={cardStyle}>
          <h3 style={{ color: COLORS.text, fontSize: 14, fontWeight: 700, margin: "0 0 14px" }}>Ingresos por bodega</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.por_bodega} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="nombre" width={110} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="ingresos" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <p style={{ fontSize: 11, color: COLORS.textDim }}>{data.nota}</p>
    </div>
  );
}
